import React from 'react';
import { JcAppRoute, JcAppRoutingConfig, JcAppRoutingUtils, JcEmptyProps } from 'jcsdk';
import { JCalProjectCategory } from 'src/services/project/models/submodel/JCalProjectCategory';

const JCalNotFoundPage = React.lazy(() => import('src/app/JCalNotFoundPage').then(m => ({ default: m.JCalNotFoundPage })));
const JCalHomePage = React.lazy(() => import('src/app/home/JCalHomePage').then(m => ({ default: m.JCalHomePage })));
const JCalBuildsPage = React.lazy(() => import('src/app/project/JCalBuildsPage').then(m => ({ default: m.JCalBuildsPage })));
const JCalProjectPage = React.lazy(() => import('src/app/project/JCalProjectPage').then(m => ({ default: m.JCalProjectPage })));
const JCalProjectEditPage = React.lazy(() => import('src/app/project/JCalProjectEditPage').then(m => ({ default: m.JCalProjectEditPage })));
const JCalProjectCreatePage = React.lazy(() => import('src/app/project/JCalProjectEditPage').then(m => ({ default: m.JCalProjectCreatePage })));
const JCalSignInPage = React.lazy(() => import('src/app/auth/JCalSignInPage').then(m => ({ default: m.JCalSignInPage })));

export interface JCalProjectPageProps {
    readonly projectId: string;
    /** The photo open in the lightbox. */
    readonly photo?: string;
}

export interface JCalProjectEditPageProps {
    readonly projectId: string;
}

export interface JCalProjectCreatePageProps {
    readonly category: JCalProjectCategory;
}

export interface JCalSignInPageProps {
    readonly returnTo?: string;
}

const HOME_ROUTE: JcAppRoute<JcEmptyProps> = {
    component: JCalHomePage,
    template: "/",
    propsFromPath: () => ({}),
    pathFromProps: () => "/",
};

const BUILDS_ROUTE: JcAppRoute<JcEmptyProps> = {
    component: JCalBuildsPage,
    template: "/builds",
    propsFromPath: () => ({}),
    pathFromProps: () => "/builds",
};

/** Each category's projects live under their own URL segment (`/builds/…`, `/software/…`). */
const projectRoutes = (category: JCalProjectCategory, segment: string) => {
    const view: JcAppRoute<JCalProjectPageProps> = {
        component: JCalProjectPage,
        template: `/${segment}/:projectId`,
        propsFromPath: (path: string) => ({
            projectId: JcAppRoutingUtils.extractUrlParams(path, `/${segment}/:projectId`).projectId ?? "",
            photo: JcAppRoutingUtils.extractQueryParams(path).photo,
        }),
        pathFromProps: ({ projectId, photo }) =>
            `/${segment}/${projectId}${photo ? `?photo=${encodeURIComponent(photo)}` : ""}`,
    };
    const edit: JcAppRoute<JCalProjectEditPageProps> = {
        component: JCalProjectEditPage,
        template: `/${segment}/:projectId/edit`,
        propsFromPath: (path: string) => ({
            projectId: JcAppRoutingUtils.extractUrlParams(path, `/${segment}/:projectId/edit`).projectId ?? "",
        }),
        pathFromProps: ({ projectId }) => `/${segment}/${projectId}/edit`,
    };
    const create: JcAppRoute<JCalProjectCreatePageProps> = {
        component: JCalProjectCreatePage,
        template: `/${segment}/new`,
        propsFromPath: () => ({ category }),
        pathFromProps: () => `/${segment}/new`,
    };
    return { view, edit, create };
};

const BUILD_ROUTES = projectRoutes(JCalProjectCategory.BUILD, "builds");
const SOFTWARE_ROUTES = projectRoutes(JCalProjectCategory.SOFTWARE, "software");

const SIGN_IN_ROUTE: JcAppRoute<JCalSignInPageProps> = {
    component: JCalSignInPage,
    template: "/signin",
    propsFromPath: (path: string) => ({ returnTo: JcAppRoutingUtils.extractQueryParams(path).returnTo }),
    pathFromProps: ({ returnTo }) => (returnTo ? `/signin?returnTo=${encodeURIComponent(returnTo)}` : "/signin"),
};

export const jcalRoutes = {
    HOME: HOME_ROUTE,
    BUILDS: BUILDS_ROUTE,
    BUILD: BUILD_ROUTES.view,
    BUILD_EDIT: BUILD_ROUTES.edit,
    BUILD_CREATE: BUILD_ROUTES.create,
    SOFTWARE: SOFTWARE_ROUTES.view,
    SOFTWARE_EDIT: SOFTWARE_ROUTES.edit,
    SOFTWARE_CREATE: SOFTWARE_ROUTES.create,
    SIGN_IN: SIGN_IN_ROUTE,
} as const;

export type JCalRoutes = typeof jcalRoutes;
export type JCalRouteKey = keyof JCalRoutes;

/** The route keys for a category's view/edit/create pages. */
export const JCAL_PROJECT_ROUTE_KEYS = {
    [JCalProjectCategory.BUILD]: { view: "BUILD", edit: "BUILD_EDIT", create: "BUILD_CREATE" },
    [JCalProjectCategory.SOFTWARE]: { view: "SOFTWARE", edit: "SOFTWARE_EDIT", create: "SOFTWARE_CREATE" },
} as const;

export const jcalAppRoutingConfig: JcAppRoutingConfig<JCalRoutes> = {
    defaultRoute: "HOME",
    notFoundPage: JCalNotFoundPage,
    routes: jcalRoutes,
};
